import React, { useEffect, useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import toast from "react-hot-toast";
import {
  User,
  Mail,
  Phone,
  ArrowRight,
  TrendingUp,
  Sparkles,
  ShieldCheck,
  Fingerprint,
  Loader2,
} from "lucide-react";
import logo from "../assets/logo64.png";
import { supabase } from "../lib/supabase";
import { Console } from "console";

const Register: React.FC = () => {
  // Form States
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [method, setMethod] = useState<"email" | "phone">("email");
  const [referralCode, setReferralCode] = useState("");
  const [loading, setLoading] = useState(false);

  // View Toggle & OTP States
  const [showOtp, setShowOtp] = useState(false);
  const [otp, setOtp] = useState<string[]>(Array(6).fill(""));
  const otpRefs = useRef<Array<HTMLInputElement | null>>([]);

  const { signInWithOTP, signInWithPhoneOTP, verifyOTP } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const refCode = queryParams.get('ref');
    if (refCode) setReferralCode(refCode);
  }, []);

  // STEP 1: Request OTP
  const handleRequestOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (method === "email") {
        await signInWithOTP(email);
      } else {
        // Ensure your AuthContext handles phone formatting if required by Supabase
        await signInWithPhoneOTP(phone);
      }

      // Store transient data to be used after verification
      sessionStorage.setItem(
        "registrationData",
        JSON.stringify({ name, email, phone, referralCode, method }),
      );

      setShowOtp(true);
      toast.success(`Verification code sent to your ${method}`);

      setTimeout(() => otpRefs.current[0]?.focus(), 150);
    } catch (error: any) {
      toast.error(error.message || "Failed to initiate KYC");
    } finally {
      setLoading(false);
    }
  };

  // STEP 2: Verify OTP and Create Profile
  const handleVerifyAndRegister = async (e: React.FormEvent) => {
    e.stopPropagation()
    e.preventDefault();
    setLoading(true);
    try {
      const code = otp.join("");

      let verifyData, verifyError;

      if (method === "email") {
        console.log("email:",email);
        console.log("code:",code);

        ({ data: verifyData, error: verifyError } =
          await supabase.auth.verifyOtp({
            email,
            token: code,
            type: "magiclink", // or "magiclink", "recovery", etc. depending on flow
          }));
      } else if (method === "phone") {
        ({ data: verifyData, error: verifyError } =
          await supabase.auth.verifyOtp({
            phone,
            token: code,
            type: "sms",
          }));
      }

      if (verifyError) throw verifyError;

      const user = verifyData?.user;
      if (!user) throw new Error("Verification failed: No user found.");
     
      // 2. Retrieve the metadata stored in Step 1
      const regData = sessionStorage.getItem("registrationData");
      if (!regData) {
        toast.error("Session expired. Please restart registration.");
        setShowOtp(false);
        return;
      }

      const { name: storedName, referralCode: storedRef } = JSON.parse(regData);

      // 3. Upsert the profile record
      const { error: profileError } = await supabase
        .from("user_profiles")
        .upsert({
          id: user.id,
          name: storedName,
          email: user.email || email,
          phone: user.phone || phone,
          referralcode: storedRef,
          plan_id: "2326cde6-c9b4-4b68-ba66-84bcde479d66",
        });

      if (profileError) throw profileError;

      // 4. Cleanup and Redirect
      sessionStorage.removeItem("registrationData");
      toast.success("Identity Verified! Welcome to Vytta.");

      // User is already logged in now, go straight to home
      navigate("/home");
    } catch (error: any) {
      console.error("Registration Error:", error);
      toast.error(error.message || "Invalid or expired verification code");
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (value: string, index: number) => {
    if (!/^[0-9]?$/.test(value)) return;
    const next = [...otp];
    next[index] = value;
    setOtp(next);
    if (value && index < 5) otpRefs.current[index + 1]?.focus();
  };

  const handleOtpKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  return (
    <div className="min-h-screen bg-[#08090a] text-white antialiased font-sans selection:bg-orange-500/30">
      <div className="fixed inset-0 bg-[radial-gradient(circle_at_50%_0%,#1a1b23_0%,transparent_50%)] pointer-events-none" />
      <div className="fixed bottom-[-10%] left-[-10%] w-[40%] h-[40%] bg-orange-500/5 rounded-full blur-[120px] pointer-events-none" />

      <nav className="sticky top-0 z-50 backdrop-blur-2xl bg-black/40 border-b border-white/5 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group no-underline">
            <div className="relative w-9 h-9">
              <div className="absolute inset-0 bg-orange-500 blur-md opacity-40 group-hover:opacity-100 transition-opacity" />
              <img
                src={logo}
                alt="V"
                className="relative w-full h-full object-cover rounded-lg border border-white/20"
              />
            </div>
            <span className="text-2xl font-black tracking-tighter italic bg-gradient-to-r from-white to-orange-500 bg-clip-text text-transparent">
              ytta
            </span>
          </Link>
        </div>
      </nav>

      <div className="max-w-xl mx-auto px-6 pt-12 pb-24 relative z-10 flex flex-col items-center">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-500/10 border border-orange-500/20 text-orange-500 text-[10px] font-black tracking-widest uppercase mb-4">
            {showOtp ? "Identity Verification" : "Institutional Access"}
          </div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tighter mb-4 italic">
            {showOtp ? "Secure " : "Begin Your "}
            <span className="bg-gradient-to-r from-white to-orange-500 bg-clip-text text-transparent">
              {showOtp ? "KYC" : "Journey"}
            </span>
          </h1>
          <p className="text-gray-500 text-sm max-w-xs mx-auto leading-relaxed">
            {showOtp
              ? `We've sent a 6-digit secure terminal code to your ${method}`
              : "Join a community of elite traders using institutional-grade signals."}
          </p>
        </div>

        <div className="w-full relative group">
          <div className="absolute -inset-1 bg-gradient-to-r from-orange-500/20 to-orange-500/0 rounded-[2.5rem] blur opacity-25 group-hover:opacity-50 transition duration-1000" />

          <div className="relative bg-white/[0.02] backdrop-blur-xl rounded-[2.5rem] p-8 md:p-12 border border-white/5 flex flex-col items-center">
            {!showOtp ? (
              <form onSubmit={handleRequestOTP} className="w-full space-y-6">
                <InputGroup
                  label="Full Name"
                  icon={<User size={18} />}
                  value={name}
                  onChange={setName}
                  placeholder="John Doe"
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputGroup
                    label="Email"
                    icon={<Mail size={18} />}
                    value={email}
                    onChange={setEmail}
                    placeholder="name@terminal.com"
                    type="email"
                  />
                  <InputGroup
                    label="Phone Number"
                    icon={<Phone size={18} />}
                    value={phone}
                    onChange={setPhone}
                    placeholder="+91 00000 00000"
                    type="tel"
                  />
                </div>

                <div className="space-y-3">
                  <label className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-500 ml-2">
                    Send OTP Code via
                  </label>
                  <div className="flex p-1 bg-white/5 rounded-2xl border border-white/5">
                    <button
                      type="button"
                      onClick={() => setMethod("email")}
                      className={`flex-1 py-3 rounded-xl flex items-center justify-center gap-2 text-[10px] font-black tracking-widest uppercase transition-all ${
                        method === "email"
                          ? "bg-white text-black shadow-lg"
                          : "text-gray-500 hover:text-white"
                      }`}
                    >
                      <Mail size={14} /> Email
                    </button>
                    <button
                      type="button"
                      onClick={() => setMethod("phone")}
                      className={`flex-1 py-3 rounded-xl flex items-center justify-center gap-2 text-[10px] font-black tracking-widest uppercase transition-all ${
                        method === "phone"
                          ? "bg-white text-black shadow-lg"
                          : "text-gray-500 hover:text-white"
                      }`}
                    >
                      <Phone size={14} /> Phone
                    </button>
                  </div>
                </div>

                <InputGroup
                  label="Referral Code"
                  icon={<Sparkles size={18} />}
                  value={referralCode}
                  onChange={setReferralCode}
                  placeholder="Optional"
                  required={false}
                />

                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-5 bg-white text-black hover:bg-orange-500 hover:text-white rounded-2xl font-black text-xs tracking-[0.3em] uppercase transition-all duration-500 disabled:opacity-20 flex items-center justify-center gap-3 shadow-xl"
                  >
                    {loading ? (
                      <Loader2 className="animate-spin" size={18} />
                    ) : (
                      <>
                        Continue to OTP <ArrowRight size={16} />
                      </>
                    )}
                  </button>
                </div>
              </form>
            ) : (
              <form
                onSubmit={handleVerifyAndRegister}
                className="w-full space-y-8 flex flex-col items-center"
              >
                <Fingerprint
                  size={48}
                  className="text-orange-500 opacity-40 animate-pulse"
                />

                <div className="flex gap-2 justify-center">
                  {otp.map((digit, i) => (
                    <input
                      key={i}
                      ref={(el) => (otpRefs.current[i] = el)}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) =>
                        handleOtpChange(e.target.value.trim(), i)
                      }
                      onKeyDown={(e) => handleOtpKeyDown(e, i)}
                      className="w-10 h-14 md:w-12 md:h-16 text-center text-2xl font-black rounded-xl bg-white/5 border border-white/10 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none text-white transition-all"
                    />
                  ))}
                </div>

                <button
                  type="submit"
                  disabled={loading || otp.includes("")}
                  className="w-full py-5 bg-orange-500 text-white rounded-2xl font-black text-xs tracking-[0.3em] uppercase shadow-xl shadow-orange-500/20 hover:scale-[1.02] active:scale-95 transition-all disabled:opacity-20"
                >
                  {loading ? "Authorizing..." : "Verify & Complete"}
                </button>

                <button
                  type="button"
                  onClick={() => setShowOtp(false)}
                  className="text-[10px] font-black text-gray-600 hover:text-white uppercase tracking-[0.2em] transition-colors"
                >
                  ← Edit Profile Details
                </button>
              </form>
            )}

            <div className="mt-8 pt-8 border-t border-white/5 text-center w-full">
              <p className="text-[10px] text-gray-500 font-bold uppercase tracking-widest">
                Protected by Vytta Secure Gateway Protocol
              </p>
            </div>
          </div>
        </div>

        <div className="mt-12 flex items-center gap-6 opacity-40 grayscale hover:grayscale-0 transition-all duration-700">
          <Indicator icon={<ShieldCheck size={14} />} label="Encrypted" />
          <div className="w-px h-3 bg-white/20" />
          <Indicator icon={<TrendingUp size={14} />} label="Real-time" />
          <div className="w-px h-3 bg-white/20" />
          <Indicator icon={<Sparkles size={14} />} label="Certified" />
        </div>
      </div>
    </div>
  );
};

const InputGroup = ({
  label,
  icon,
  value,
  onChange,
  placeholder,
  type = "text",
  required = true,
}: any) => (
  <div className="space-y-2">
    <label className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-500 ml-2">
      {label}
    </label>
    <div className="relative">
      <div className="absolute left-5 top-1/2 -translate-y-1/2 text-orange-500">
        {icon}
      </div>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full pl-14 pr-6 py-4 bg-white/[0.03] rounded-2xl border border-white/5 focus:border-orange-500 focus:ring-1 focus:ring-orange-500/50 outline-none transition-all font-bold text-sm"
        required={required}
      />
    </div>
  </div>
);

const Indicator = ({ icon, label }: any) => (
  <div className="flex items-center gap-2">
    {icon}
    <span className="text-[10px] font-black uppercase tracking-widest">
      {label}
    </span>
  </div>
);

export default Register;
